/* eslint-disable react-hooks/rules-of-hooks */
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { parseDate } from "@lyfegen-platform/utils";
import { useFieldValue } from "formik";
import * as Yup from "yup";
import { ControlState } from "../models/flexible/control-state";
import { combinePaths } from "../models/flexible/utils";
import { CoverageRequestTherapyDecision } from "../models/generated/graphql";
import { getPathPrefix } from "../utils/get-path-prefix";
import { getCoverageBoundaries } from "../utils/negotiation-rounds";
import { renderRequiredMessage } from "../utils/render-required-message";
import { GeneralFieldController } from "./general-field-controller";
import { GetPatientEntitiesByEntityId } from "./types/GetPatientEntitiesByEntityId";
import { GetPatientEntitiesById } from "./types/GetPatientEntitiesById";

type InitialValuesType = GetPatientEntitiesByEntityId | GetPatientEntitiesById;

export class TherapyTrialStartDateFieldController extends GeneralFieldController {
	override useControlState(path: string): ControlState {
		const [decisionTherapy] = useFieldValue(combinePaths(getPathPrefix(path, 1), AttributeIdentifiers.DecisionTherapy));

		return decisionTherapy === CoverageRequestTherapyDecision.Trial ? "enabled" : "hidden";
	}

	override createValidationRule(_initialData: InitialValuesType, translate: (key: string) => string): Yup.AnySchema {
		const isPreviousRequestTrial = this.parent.context.cases
			.flatMap(getCoverageBoundaries)
			.filter(({ formCreatedOn }) => !this.parent.context.flexEntity || formCreatedOn < this.parent.context.flexEntity?.id)
			.some(({ decision }) => decision === CoverageRequestTherapyDecision.Trial);
		const isExistingCase = !!this.parent.context.cases.length;

		return super
			.createFieldValidationRuleByDataType(translate)
			.when(AttributeIdentifiers.DecisionTherapy, {
				is: CoverageRequestTherapyDecision.Trial,
				then: (schema: Yup.AnySchema) => schema.nullable().required(renderRequiredMessage(this.schema.attribute.identifier, translate)),
			})
			.test({
				name: "cannot-be-less-than",
				message: `${translate("cannot-be-less-than")} ${translate("requestReceiptDate")}`,
				test: (value, ctx) => {
					if (ctx.parent.therapyDecision === CoverageRequestTherapyDecision.Approved) return true;
					if (isPreviousRequestTrial && isExistingCase) return true;
					if (
						value &&
						ctx.options.context.mergedFormValues.requestReceiptDate &&
						(value as Date) < parseDate(ctx.options.context.mergedFormValues.requestReceiptDate || "")
					)
						return false;
					return true;
				},
			});
	}

	private isTherapyTrialStartDate = () =>
		this.parent.context.cases
			.flatMap(getCoverageBoundaries)
			.filter(({ formCreatedOn }) => !this.parent.context.flexEntity || formCreatedOn < this.parent.context.flexEntity?.createdOn)
			.some(({ decision }) => decision === CoverageRequestTherapyDecision.Trial);
}
